<script setup lang="ts">

import { useBackButton } from "@app/Components/Utilities";
import { useViewModelInstance } from "@app/MyGarage/Types/MyGarageViewModelStore";

useBackButton();

const viewModel = await useViewModelInstance();

</script>

<template>

    <div class="bg-white dark:bg-gray-850 tracking-wider text-sm lg:text-base
                 w-full h-full overflow-y-auto flex flex-col pb-12">

        <div class="flex justify-center items-center my-6">
            <strong class="text-lg font-bold">Flat Tyre?</strong>
        </div>     

        <div class="flex flex-col w-full gap-3 px-4 lg:px-6">
        
            <p>
                Getting a flat tyre is never convenient. Lets hope we can get you back on the road ASAP! 
            </p>

            <div v-if="viewModel.carePlan.roadsideAssistance.isEnabled">
                <p>
                    If you're not comfortable changing the tyre yourself or if you don't have the necessary 
                    tools, you can call for roadside assistance. If you don't have any Carbucks has partnered
                    with Roadside Assist providers that will be able to assist even if you aren't a member.
                </p>

                <div class="flex justify-center py-4">
                    <RoadsideAssistanceButton /> 
                </div>
            </div>
                
            <div class="flex flex-col w-full gap-3 dark:text-gray-300">
            
                <p>
                    Here are some steps you can take to address the situation:
                </p>        

                <ul class="pl-3 lg:pl-5 flex flex-col w-full gap-2 dark:text-gray-300 list-decimal">
                    
                    <li>
                        <span class="dark:text-white">Find a safe location:</span>
                        Safely pull over to the side of the road or into a parking
                        lot. Make sure you're in a safe location away from traffic.
                    </li>

                    <li>
                        <span class="dark:text-white">Turn on hazard lights:</span>
                        Turn on your hazard lights to alert other drivers that your 
                        vehicle is stopped.
                    </li>        

                    <li>
                        <span class="dark:text-white">Gather tools:</span>
                        Locate the tools you'll need to change the tyre. These typically 
                        include a jack, lug wrench, and spare tyre. These tools are usually 
                        found in the trunk of your car, along with the spare tyre. Some 
                        cars may have the spare tyre stored in a different location, 
                        such as underneath the vehicle.
                    </li>        

                    <li>
                        <span class="dark:text-white">Secure the vehicle:</span>
                        Before jacking up the car, make sure it's in park (if automatic) 
                        or in gear (if manual), and engage the parking brake to prevent 
                        the vehicle from rolling.
                    </li>        

                    <li>
                        <span class="dark:text-white">Loosen the lug nuts:</span>
                        Loosen the lug nuts on the flat tyre, but do not remove them completely 
                        yet. Use the lug wrench to turn each lug nut counterclockwise, but keep 
                        the tyre on the ground for stability.
                    </li>        

                    <li>
                        <span class="dark:text-white">Jack up the car:</span>
                        Safely pull over to the side of the road or into a parking
                        lot. Make sure you're in a safe location away from traffic.
                    </li>        

                    <li>
                        <span class="dark:text-white">Remove the lug nuts and tyre:</span>
                        Once the vehicle is raised, finish removing the lug nuts and set them aside. 
                        Carefully remove the flat tyre from the wheel hub and set it aside.
                    </li>        

                    <li>
                        <span class="dark:text-white">Install the spare tyre:</span>
                        Align the spare tyre with the wheel studs and slide it onto the wheel 
                        hub. Replace the lug nuts and tighten them by hand.
                    </li>        

                    <li>
                        <span class="dark:text-white">Lower the vehicle:</span>
                        Carefully lower the vehicle using the jack until the spare tyre is 
                        touching the ground, but not bearing the full weight of the vehicle.
                    </li>        

                    <li>
                        <span class="dark:text-white">Tighten the lug nuts:</span>
                        Use the lug wrench to tighten the lug nuts in a star pattern. This 
                        ensures even tightening and helps prevent the wheel from 
                        being mounted unevenly.
                    </li>        

                    <li>
                        <span class="dark:text-white">Lower the vehicle completely:</span>
                        Finish lowering the vehicle all the way to the ground and remove the jack.
                    </li>        

                    <li>
                        <span class="dark:text-white">Check the tyre pressure:</span>
                        Before driving, check the air pressure in the spare tyre to ensure 
                        it's safe to drive on. Adjust the pressure if necessary.
                    </li>        

                    <li>
                        <span class="dark:text-white">Drive cautiously:</span>
                        Spare tyres are usually smaller and not designed for long-term use, so 
                        drive cautiously and avoid high speeds.
                    </li>        

                    <li>
                        <span class="dark:text-white">Repair or replace the flat tyre:</span>
                        After changing the tyre, you'll need to repair or replace the flat tyre 
                        as soon as possible. If it's repairable, you can take it to a tyre shop 
                        to have it patched. If it's not repairable, you'll need to replace it 
                        with a new tyre.
                    </li>    

                </ul>

                <p>
                    If you're not comfortable changing the tyre yourself or if you don't have the 
                    necessary tools, you can call for roadside assistance or a tow truck to help you out.
                </p>        

            </div>

        </div>

    </div>   
    
</template>
